<template>
  <div :data-qa="`orders-orderitem-${orderline.orderlineId}`">
    <CancelOrderPendingPaymentModal :orderline-id="orderline.orderlineId" />
    <InvoiceRequestedModal :orderline-id="orderline.orderlineId" />

    <RevCard>
      <OrderlineContainer
        data-test="orderline-container"
        :orderline="orderline"
      />

      <MobilePlan v-if="hasMobilePlan" data-test="mobile-plan-orderline" />

      <RevDivider class="block" />

      <RevAccordionItem
        :data-qa="`orders-orderitem-${orderline.orderlineId}-showinfo`"
        data-test="orderline-show-more-details"
        @click="getOrderlineDetails"
      >
        <template #title>
          {{ accordionItemTitle }}
        </template>

        <template #body>
          <RevSpinner
            v-if="pending"
            :alternative-text="i18n(translations.loading)"
            class="mx-auto my-12"
            data-test="`orderline-card-details-loader`"
          />

          <Info
            v-if="orderlineDetails && !pending"
            :orderline="orderlineDetails"
          />
        </template>
      </RevAccordionItem>
    </RevCard>
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import type { Orderline } from '@backmarket/http-api/src/api-specs-my-orders-api/types/orderlineListResponse'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevSpinner } from '@ds/components/Spinner'

import translations from './OrderlineCard.translations'
import Info from './components/Info/Info.vue'
import MobilePlan from './components/MobilePlan/MobilePlan.vue'
import OrderlineContainer from './components/OrderlineContainer/OrderlineContainer.vue'
import { useOrderlineDetails } from './composables/useOrderlineDetails'
import CancelOrderPendingPaymentModal from './modals/CancelOrderPendingPaymentModal/CancelOrderPendingPaymentModal.vue'
import InvoiceRequestedModal from './modals/InvoiceRequestedModal/InvoiceRequestedModal.vue'

export interface OrderlineCardProps {
  orderline: Orderline
}

const props = defineProps<OrderlineCardProps>()

const i18n = useI18n()
const logger = useLogger()

const { FF_ENABLED_MOBILE_PLAN_ORDERLINE } = useRuntimeConfig().public

const accordionItemCollapse = ref<boolean>(true)

const {
  data: orderlineDetails,
  execute,
  error,
  pending,
} = useOrderlineDetails({
  orderlineId: props.orderline.orderlineId,
})

if (error.value) {
  if (error instanceof Error) {
    logger.error(`[SCAN] [Orderline #${props.orderline.orderlineId}`, { error })
  }
}

const hasMobilePlan = FF_ENABLED_MOBILE_PLAN_ORDERLINE === MarketCountryCode.FR

const accordionItemTitle = computed(() =>
  accordionItemCollapse.value
    ? i18n(translations.orderlineCardSeeMoreDetails)
    : i18n(translations.orderlineCardHideMoreDetails),
)

async function getOrderlineDetails() {
  if (accordionItemCollapse.value) {
    await execute()
  }

  accordionItemCollapse.value = !accordionItemCollapse.value
}
</script>
